/**
 * «Armar el presupuesto» (OPUS ci, idea 1): el borrador de la hoja en un clic, en cuatro pasos.
 *
 *  1. Antes de armar: lo que el plano no trae y hace falta para medir (unidad del dibujo, alto de los
 *     muros), qué elementos se van a buscar, lo que no se va a tocar, y DOS PREGUNTAS: ¿también sus
 *     complementarios (lo que cada concepto implica: dala, excavación, aplanado…)? ¿también lo que el
 *     plano no dibuja (instalaciones básicas)?
 *  2. Armando: por cada elemento que falta, los «Recomendados» de siempre (la misma consulta del panel),
 *     de lo más grande a lo más chico; con sus complementarios si se pidieron. Se puede detener.
 *  3. Revisión: nada entra a la hoja sin pasar por aquí. Cada concepto con sus complementarios colgados
 *     y, aparte, lo que no trae el plano. Por renglón: una de las opciones del catálogo, «Generar con
 *     IA» o «No agregar», y su cantidad editable.
 *  4. Resumen: lo que entró y cuántos necesitan IA (el mismo lote de la hoja, con su confirmación).
 *
 * La lógica de medir, proponer y asignar vive en PlanosPage (la misma del panel); aquí solo el recorrido.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Option,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  ProgressBar,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowUndo16Regular, Checkmark16Filled, ChevronDown20Regular, Delete16Regular, Info16Regular, LightbulbRegular, Search16Regular, Sparkle20Regular, Warning16Filled } from '@fluentui/react-icons';
import { OCULTAR_COSTO_IA } from '../lib/flags.js';
import { TITULO_VEREDICTO_CHEQUEO, juntarFotos, juntarTodas, type AgrupacionDeReceta, type ChequeoDelPlano, type FotoGenerador, type MagnitudDelConcepto } from '@supernova/shared';

/**
 * Cuántos elementos se consultan a la vez. Medido en frío (casa FZK, 16/10): con 3 el armado tardaba 4.5–6 min; con 6, ≈ 3 min; con 8
 * no mejora (≈ 3.3 min). Sin errores 429 del proveedor ni del servidor (tope global 300/min). Más no aporta: el resto es la cola final.
 */
const EN_PARALELO = 6;

/**
 * Lo que tira `proponer`/`proponerNoDibujado` cuando el servidor dice que la bolsa se acabó: la corrida
 * se DETIENE (no se siguen pidiendo consultas que van a fallar) y lo armado hasta ahí se revisa igual.
 */
export const SIN_SALDO = 'SIN_SALDO';

export interface CandidatoArmado {
  key: string;
  /** 'capa' o 'bloque' en un DWG; en un IFC, siempre 'capa' (un tipo del modelo). */
  tipo: 'capa' | 'bloque';
  nombre: string;
  /** La medida con la que se ordenó («136,3 m²», «12 pza»). */
  medida: string;
}

export interface OpcionArmado {
  resourceId: string;
  catalogId: string;
  clave: string;
  descripcion: string;
  unidad: string | null;
  precio: number | null;
  razon: string | null;
  /** La IA lo juzgó el mismo elemento (aunque cambie la medida). null = no lo dijo. */
  mismoElemento?: boolean | null;
  /** Si no es exacto: ¿la diferencia cambia el precio de forma importante? false = de detalle. */
  afectaPrecio?: boolean | null;
  /** ¿Mismo sistema constructivo que el que pide el plano? false = otro. null = el plano no lo dice. */
  mismoSistema?: boolean | null;
}

/**
 * ¿Esta opción del catálogo puede ir en lugar de lo que pide el plano? Con precio (un equivalente sin precio
 * no es equivalente, AI-36) y, si no es exacto, el mismo elemento HECHO CON EL MISMO SISTEMA: si el plano
 * dice de qué y cómo se construye y el catálogo solo trae otro sistema (un muro de concreto armado colado
 * por uno de piezas de concreto ligero), no es «lo más parecido», es otra obra: va generado con IA. Única regla.
 */
export function sirveDelCatalogo(o: OpcionArmado, exacto: boolean): boolean {
  return o.precio != null && o.precio > 0 && (exacto || (o.mismoElemento === true && o.mismoSistema !== false));
}

export type TipoFilaArmado = 'principal' | 'complemento' | 'noDibujado';
export type Eleccion = number | 'ia' | 'fuera';

export interface CantidadArmado {
  /** null = falta un dato para medirlo: la escribe el usuario. */
  cantidad: number | null;
  revisar: boolean;
  nota: string | null;
  /** Números generadores: la foto de la medición (sus parciales dan `cantidad`). null = no se pudo desglosar. */
  foto?: FotoGenerador | null;
}

/** Un renglón propuesto (principal, complementario o algo que el plano no dibuja). */
export interface PropuestaArmado {
  /** Único en la corrida. */
  key: string;
  tipo: TipoFilaArmado;
  /** Elemento del plano del que sale (capa/bloque); null en lo que no trae el plano. */
  elementoKey: string | null;
  /** Complementario: la `key` del principal del que cuelga. */
  padre: string | null;
  /**
   * Principal: el USO del elemento cuando el modelo lo dice (IFC: clase + tipo predefinido, p. ej. losa
   * de azotea vs. de entrepiso). Los complementarios solo se juntan entre elementos del mismo uso.
   */
  uso?: string;
  nombre: string;
  medida: string;
  opciones: OpcionArmado[];
  eleccion: Eleccion;
  /** Debajo del concepto: por qué no hay uno exacto, por qué no se agrega, de dónde sale… */
  nota: string | null;
  /** Lo que entra si se genera con IA. */
  sintetico: { descripcion: string; unidad: string | null };
  /**
   * La cantidad. En un principal depende del concepto elegido (se vuelve a medir al cambiarlo); en un
   * complementario o en lo que no trae el plano sale de su propia regla y no cambia con el concepto.
   */
  cantidad: CantidadArmado;
  /** Partida en que entra (lo que no trae el plano va en la de su grupo). */
  partida?: string;
  /** Frase con que se busca en el catálogo (complementarios y lo no dibujado): para buscar a pedido. */
  consulta?: string;
  /** «No agregar» lo eligió la persona (no lo propuso el asistente). */
  quitadoPorTi?: boolean;
  /** Lo que tenía antes de quitarlo (para «Restablecer»): concepto y cantidad, tal cual. */
  eleccionPrevia?: Eleccion;
  cantidadPrevia?: string;
  /** Complementario juntado en todo el plano: TODOS los principales que lo piden (cuelga del primero que entre). */
  padres?: string[];
  /**
   * Un renglón por CONCEPTO: los elementos del plano que junta (los tipos del modelo que llevan el mismo
   * concepto y unidad). Su cantidad es la suma. Ausente o de uno = un solo elemento (`elementoKey`).
   */
  elementos?: string[];
  /** «Conceptos por tipo»: la fila la decidió lo que la persona asignó (en este plano o en otro proyecto), no el motor. */
  deTipos?: DeTipos;
}

/** De dónde salió una fila que no decidió el motor (memoria de tipos). Se guarda en el borrador. */
export interface DeTipos {
  fuente: 'pieza_plano' | 'tipo_plano' | 'tipo_memoria' | 'clase_plano';
  /** Solo con fuente 'tipo_memoria': la receta recordada (para contar su uso). */
  memoriaId?: string;
  version?: number;
  /** El concepto recordado: la magnitud solo vale mientras siga elegido ese. */
  resourceId?: string;
  magnitud?: MagnitudDelConcepto | null;
  agrupacion?: AgrupacionDeReceta | null;
}

/** Lo que se guarda como borrador (por plano) para retomarlo al volver a abrir el asistente. */
export interface BorradorArmado {
  fecha: string;
  op: OpcionesDeArmado;
  filas: PropuestaArmado[];
  cantidades: Record<string, string>;
  duracion: number | null;
  /** SOLO staff: lo que costó la corrida (se guarda para verlo al retomar el borrador). */
  costo?: { usd: number; llamadas: number } | null;
}

export interface FilaParaAgregar {
  key: string;
  tipo: TipoFilaArmado;
  elementoKey: string | null;
  /** Complementario: todos los principales que lo piden (cuelga del primero que haya entrado). */
  padres?: string[];
  /** Todos los elementos que junta (ver `PropuestaArmado.elementos`). */
  elementos?: string[];
  padre: string | null;
  opcion: OpcionArmado | null;
  sintetico: PropuestaArmado['sintetico'] | null;
  cantidad: number;
  revisar: boolean;
  partida?: string;
  /** Números generadores: la foto de la medición (de lo que midió el plano). */
  foto?: FotoGenerador | null;
  /** La cantidad la escribió la persona en la revisión (la foto queda como «lo que midió el plano»). */
  cantidadEscritaAMano?: boolean;
  /** «Conceptos por tipo»: lo recordado que viaja con la fila (magnitud, ejes de agrupación, receta de origen). */
  deTipos?: DeTipos;
}

export interface PrevioArmado {
  chequeo: ChequeoDelPlano | null;
  /** Catálogos de referencia del plano, en su orden. Vacío = no se puede armar. */
  catalogos: string[];
  /** DWG con unidad dudosa: hay que contestarla antes (si no, nada se puede medir). */
  escala: { opciones: { unidad: string; nombre: string; consecuencia: string }[]; elegir: (unidad: string) => Promise<void> } | null;
  /** DWG con muros sin alto conocido: el alto con que se saca su área. */
  altura: { valor: string; deDonde: string | null; cambiar: (v: string) => void } | null;
  sinDecidir: number;
  yaConConcepto: number;
  /** «Asignar conceptos a elementos»: lo que ya se sabe de los tipos del plano y el único punto de entrada al diálogo. null = la función no aplica. */
  tipos?: { reconocidos: number; sugeridos: number; conflictos: number; noVigentes: number; total: number; abrir: () => void } | null;
  /** IFC: los elementos son tipos del modelo; DWG: capas y bloques. */
  esIfc: boolean;
  /** false = no aplica en este plano (no se pregunta). */
  puedeNoDibujado: boolean;
  /**
   * Lo que el plano deja en duda y conviene decidir ANTES de armar (lo arma PlanosPage con lo que ya
   * sabe del plano): qué no se sabe si se construye, una capa tomada como dibujo que encierra área, una
   * capa que repite lo que cuentan sus bloques. Contestar cambia la lista de elementos a buscar.
   */
  preguntas: PreguntaArmado[];
  /** Una línea sobre las preguntas de material (revisando el catálogo, qué grupos no se preguntaron, o que no se pudo revisar). */
  avisoMaterial?: string | null;
  /** true mientras se revisa el catálogo para saber qué preguntar: se muestra que está trabajando y «Armar» espera. */
  revisandoMaterial?: boolean;
}

export interface PreguntaArmado {
  key: string;
  /** La pregunta, en una línea. */
  titulo: string;
  /** Por qué se pregunta (lo que dice el plano). */
  detalle: string;
  opciones: { texto: string; recomendada?: boolean; elegir: () => void }[];
  /** Pregunta que se contesta escribiendo (p. ej. el material que el modelo no dice). */
  entrada?: {
    valor: string;
    placeholder: string;
    cambiar: (v: string) => void;
    /** Si la persona escribió algo, «Armar» lo toma como respuesta (en vez de la recomendada). */
    confirmar?: () => void;
    /** Respuestas para tocar en vez de escribir (las toma de lo que el sistema ya conoce, no de una lista nueva). */
    sugerencias?: string[];
    /** Qué pasa si no se contesta (se dice debajo de la pregunta). */
    siNoContestas?: string;
  };
}

export interface OpcionesDeArmado {
  complementarios: boolean;
  noDibujado: boolean;
}

type Fila = PropuestaArmado & { cantidadStr: string; midiendo: boolean };

const useStyles = makeStyles({
  surface: { width: '1080px', maxWidth: 'calc(100vw - 32px)' },
  bloque: { display: 'flex', flexDirection: 'column', gap: '10px' },
  item: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '13px', lineHeight: 1.45 },
  icono: { flexShrink: 0, marginTop: '2px' },
  sub: { fontSize: '12px', color: 'var(--text-tertiary)' },
  pregunta: {
    border: '1px solid var(--border-subtle)',
    borderRadius: '6px',
    padding: '8px 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  tablaCaja: { maxHeight: '52vh', overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: '6px' },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' },
  th: {
    position: 'sticky',
    top: 0,
    background: 'var(--bg-raised)',
    textAlign: 'left',
    padding: '6px 8px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    borderBottom: '1px solid var(--border-subtle)',
    zIndex: 1,
  },
  seccion: { padding: '6px 8px', fontWeight: 700, background: 'var(--bg-raised)', borderBottom: '1px solid var(--border-subtle)' },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' },
  num: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  resumen: { display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'baseline', fontSize: '13px', color: 'var(--text-secondary)' },
  total: { fontSize: '20px', fontWeight: 700, color: 'var(--text-primary)' },
});

const pesos = (n: number) => n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });
const numero = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
};
const fmtCant = (n: number) => String(Math.round(n * 100) / 100);
const reloj = (seg: number) => `${Math.floor(seg / 60)}:${String(seg % 60).padStart(2, '0')}`;
const plural = (n: number, uno: string, varios: string) => (n === 1 ? `1 ${uno}` : `${n} ${varios}`);

/** Concepto + unidad de un renglón: lo que decide si dos renglones son el mismo. null = no va a la hoja. */
function claveDeConcepto(f: PropuestaArmado): string | null {
  if (f.eleccion === 'fuera') return null;
  const o = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : undefined;
  const concepto = o
    ? `cat:${o.resourceId}`
    : `ia:${f.sintetico.descripcion.replace(/\s*\([^)]*\)\s*$/, '').trim().toLowerCase()}`;
  return `${concepto}|${((o ? o.unidad : f.sintetico.unidad) ?? '').toLowerCase()}`;
}

const sumar = (xs: (number | null)[]): number | null =>
  xs.some((x) => x == null) ? null : Math.round(xs.reduce<number>((a, x) => a + (x ?? 0), 0) * 1000) / 1000;

/**
 * Un renglón por CONCEPTO (decisión de Sebastián, 2026-10-02): un modelo trae muchos tipos del mismo
 * elemento (varios tipos de viga, de muro) y cada uno salía con su renglón; el FZK dio 376 renglones de
 * 132 conceptos distintos. Se juntan los principales con el mismo concepto y unidad (cantidad = suma, se
 * recuerdan sus elementos), sus complementarios con el mismo concepto bajo el MISMO principal juntado, y
 * lo que no trae el plano con el mismo concepto. Lo que va «No agregar» no se junta. Puro.
 */
export function agruparPorConcepto(filas: PropuestaArmado[]): PropuestaArmado[] {
  const grupoDe = new Map<string, string>(); // key de principal → key del principal que lo junta
  const porClave = new Map<string, PropuestaArmado>();
  const out: PropuestaArmado[] = [];
  const veces = new Map<PropuestaArmado, number>();
  const juntar = (clave: string | null, f: PropuestaArmado, padre: string | null) => {
    // Un complementario se junta en TODO el plano (no por principal): el «aplanado de mezcla» de todos
    // los muros es una partida. Cuelga del primer principal; `padres` recuerda todos.
    const k = clave == null ? null : `${f.tipo}|${clave}`;
    const ya = k ? porClave.get(k) : undefined;
    if (!ya) {
      const nuevo: PropuestaArmado = {
        ...f,
        padre,
        padres: padre ? [padre] : undefined,
        elementos: f.elementoKey ? [f.elementoKey] : f.elementos,
      };
      if (k) porClave.set(k, nuevo);
      veces.set(nuevo, 1);
      out.push(nuevo);
      return nuevo;
    }
    const n = (veces.get(ya) ?? 1) + 1;
    veces.set(ya, n);
    const elementos = [...(ya.elementos ?? []), ...(f.elementoKey && !(ya.elementos ?? []).includes(f.elementoKey) ? [f.elementoKey] : [])];
    Object.assign(ya, {
      elementos,
      padres: padre && !(ya.padres ?? []).includes(padre) ? [...(ya.padres ?? []), padre] : ya.padres,
      cantidad: {
        cantidad: sumar([ya.cantidad.cantidad, f.cantidad.cantidad]),
        revisar: ya.cantidad.revisar || f.cantidad.revisar,
        nota: ya.cantidad.nota ?? f.cantidad.nota,
        // La foto del renglón junta las de sus elementos (si alguno no tiene, el renglón queda sin foto).
        ...(ya.cantidad.foto !== undefined || f.cantidad.foto !== undefined
          ? { foto: ya.cantidad.foto && f.cantidad.foto ? juntarFotos(ya.cantidad.foto, f.cantidad.foto) : null }
          : {}),
      },
      medida: ya.tipo === 'principal' ? `${elementos.length} tipos del plano` : `de ${n} elementos`,
    });
    return ya;
  };
  for (const f of filas.filter((x) => x.tipo === 'principal')) {
    // Solo dentro del mismo uso: la losa sobre el terreno y la de entrepiso son partidas distintas aunque el
    // catálogo les dé el mismo concepto (se sumaban en un renglón de 219.84 m²).
    const clave = claveDeConcepto(f);
    const g = juntar(clave ? `${f.uso ?? ''}#${clave}` : null, f, null);
    grupoDe.set(f.key, g.key);
    if (g.key !== f.key && (g.elementos?.length ?? 0) === 2) g.nombre = `${g.nombre} y otros`;
  }
  // Cada partida de lo que no trae el plano es su propio «uso»: la impermeabilización de la azotea no se
  // junta con nada del entrepiso aunque caiga en el mismo concepto.
  const usoDe = new Map(
    filas
      .filter((x) => x.tipo === 'principal' || x.tipo === 'noDibujado')
      .map((x) => [x.key, x.tipo === 'noDibujado' ? x.key : x.uso ?? '']),
  );
  for (const f of filas.filter((x) => x.tipo === 'complemento')) {
    const padre = grupoDe.get(f.padre ?? '') ?? f.padre;
    // Solo entre elementos del mismo uso: la impermeabilización de la azotea no suma la losa de entrepiso.
    const uso = usoDe.get(f.padre ?? '') ?? '';
    // Lo apagado se junta por su nombre (la misma necesidad repetida en cada tipo).
    juntar(`${uso}#${claveDeConcepto(f) ?? `fuera:${f.nombre.trim().toLowerCase()}`}`, f, padre);
  }
  // Lo que no trae el plano NO se junta: cada renglón es una partida distinta aunque caiga en el mismo
  // concepto (la losa de entrepiso y la de azotea; el firme de planta baja y el piso de planta alta).
  for (const f of filas.filter((x) => x.tipo === 'noDibujado')) juntar(null, f, null);
  // Cada principal seguido de sus complementarios.
  const hijos = new Map<string, PropuestaArmado[]>();
  for (const f of out) if (f.tipo === 'complemento') (hijos.get(f.padre ?? '') ?? hijos.set(f.padre ?? '', []).get(f.padre ?? '')!).push(f);
  const orden: PropuestaArmado[] = [];
  for (const f of out) {
    if (f.tipo === 'complemento') continue;
    orden.push(f);
    if (f.tipo === 'principal' || f.tipo === 'noDibujado') orden.push(...(hijos.get(f.key) ?? []));
  }
  return orden;
}

/**
 * Lo que se manda a la hoja de una lista de propuestas ya juntadas por concepto, SIN revisión manual («Poner en la hoja lo
 * asignado»): entra lo medido tal cual (sin cantidad = 0, marcada para revisar), un «No agregar» no entra y un complementario
 * solo entra si alguno de sus principales entra. Puro. La misma regla que el botón «Agregar a la hoja» del asistente.
 */
export function filasParaAgregarDe(propuestas: readonly PropuestaArmado[]): FilaParaAgregar[] {
  const principales = new Set(propuestas.filter((f) => f.tipo !== 'complemento' && f.eleccion !== 'fuera').map((f) => f.key));
  const lista: FilaParaAgregar[] = [];
  for (const f of propuestas) {
    if (f.eleccion === 'fuera') continue;
    if (f.tipo === 'complemento' && ![f.padre, ...(f.padres ?? [])].some((k) => k != null && principales.has(k))) continue;
    const cantidad = f.cantidad.cantidad ?? 0;
    const base = {
      key: f.key, tipo: f.tipo, elementoKey: f.elementoKey, elementos: f.elementos, padre: f.padre, padres: f.padres, cantidad,
      partida: f.partida, revisar: f.cantidad.revisar || cantidad === 0,
      foto: f.cantidad.foto ?? null, cantidadEscritaAMano: false,
      ...(f.deTipos ? { deTipos: f.deTipos } : {}),
    };
    if (f.eleccion === 'ia') lista.push({ ...base, opcion: null, sintetico: f.sintetico });
    else if (typeof f.eleccion === 'number') lista.push({ ...base, opcion: f.opciones[f.eleccion] ?? null, sintetico: null });
  }
  return lista;
}

function Punto({ tono, children }: { tono: 'ok' | 'mal' | 'info'; children: React.ReactNode }) {
  const s = useStyles();
  return (
    <div className={s.item}>
      <span className={s.icono} style={{ color: tono === 'ok' ? 'var(--ok)' : tono === 'mal' ? 'var(--warn)' : 'var(--text-tertiary)' }}>
        {tono === 'ok' ? <Checkmark16Filled /> : tono === 'mal' ? <Warning16Filled /> : <Info16Regular />}
      </span>
      <div>{children}</div>
    </div>
  );
}

/** SOLO staff Ecosoft: lo que costó la corrida. Nunca llega aquí para un cliente (`costoStaff` = null). */
function LineaStaff({ costo }: { costo: { usd: number; llamadas: number } }) {
  if (OCULTAR_COSTO_IA) return null;
  return (
    <div style={{ fontSize: 12, color: 'var(--privado, #6c54d6)' }}>
      Solo Ecosoft · costo de IA de esta corrida: USD{' '}
      {costo.usd.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} ({plural(costo.llamadas, 'llamada', 'llamadas')})
    </div>
  );
}

export interface ArmarPresupuestoProps {
  abierto: boolean;
  onCerrar: () => void;
  previo: PrevioArmado;
  candidatos: CandidatoArmado[];
  /** El principal del elemento y, si se pidieron, sus complementarios. */
  proponer: (c: CandidatoArmado, op: OpcionesDeArmado) => Promise<PropuestaArmado[]>;
  /** Lo que no trae el plano (una sola consulta por plano). */
  proponerNoDibujado: (op: OpcionesDeArmado) => Promise<PropuestaArmado[]>;
  /** Revisión final de la hoja armada completa (lo que falta y lo que sobra), antes de mostrarla. */
  revisar?: (filas: PropuestaArmado[]) => Promise<PropuestaArmado[]>;
  /** La cantidad de un principal con la unidad de otro concepto. */
  medir: (elementoKey: string, opcion: OpcionArmado) => Promise<CantidadArmado>;
  /** `avance` avisa en qué va (n de total, y si ya está actualizando la hoja) para que la pantalla no parezca colgada. */
  agregar: (
    filas: FilaParaAgregar[],
    avance?: (n: number, total: number, etapa: 'agregando' | 'actualizando') => void,
  ) => Promise<{ agregados: number; conIa: number; fallidos: number }>;
  /** Abre la confirmación del lote de IA de la hoja. */
  generarConIa: () => void;
  /** SOLO staff Ecosoft: cuánto costó la IA desde `desde` (ISO). null = no es staff (no se muestra nada). */
  costoStaff: ((desde: string) => Promise<{ usd: number; llamadas: number }>) | null;
  /**
   * Regla de oro: ¿queda bolsa? Se revisa al apretar «Armar», ANTES de la primera consulta (cada consulta
   * la vuelve a revisar en el servidor). `aviso` = el texto para la persona si no queda.
   */
  revisarSaldo: () => Promise<{ alcanza: boolean; aviso: string | null }>;
  /** Al terminar (o detenerse): refrescar el medidor de tokens de la barra. */
  alTerminar: () => void;
  /**
   * «Rehacer la hoja»: se arma TODO el plano y el botón final reemplaza la hoja anterior (que sigue intacta
   * hasta entonces). `pierde` = lo que se va con ella, en frases. null = completar lo que falta.
   */
  rehacer?: { pierde: string[]; renglones: number } | null;
  /** «Buscar otro concepto…»: búsqueda por TEXTO en los catálogos del plano (gratis, sin IA). */
  buscarTexto: (texto: string) => Promise<OpcionArmado[]>;
  /** Buscar a pedido el concepto de un renglón que entró apagado (no se buscó para no gastar). */
  buscarOpciones: (consulta: string) => Promise<{ opciones: OpcionArmado[]; noEsExacto: string | null }>;
  /** El borrador del plano (se guarda solo al revisar; se borra al agregar a la hoja). */
  borrador: { leer: () => BorradorArmado | null; guardar: (b: BorradorArmado) => void; borrar: () => void };
  /** Título para imprimir la revisión (nombre del plano). */
  tituloImpresion: string;
}

export function ArmarPresupuesto(p: ArmarPresupuestoProps) {
  const s = useStyles();
  const [errorAgregar, setErrorAgregar] = useState<string | null>(null);
  const [paso, setPaso] = useState<'borrador' | 'antes' | 'armando' | 'revision' | 'agregando' | 'listo'>('antes');
  const [borradorPrevio, setBorradorPrevio] = useState<BorradorArmado | null>(null);
  const [op, setOp] = useState<OpcionesDeArmado>({ complementarios: true, noDibujado: true });
  const [filas, setFilas] = useState<Fila[]>([]);
  const [hechos, setHechos] = useState(0);
  // En qué va el armado: buscando el concepto de cada elemento, o en la revisión final (lo que más tarda; antes se quedaba en «59 de 59»).
  const [etapa, setEtapa] = useState<'buscando' | 'revisando'>('buscando');
  // El avance de «Agregar a la hoja» (los renglones se guardan uno por uno y luego se actualiza la hoja).
  const [avanceAgregar, setAvanceAgregar] = useState<{ n: number; total: number; etapa: 'agregando' | 'actualizando' } | null>(null);
  const inicioAgregar = useRef<number | null>(null);
  const [segundosAgregar, setSegundosAgregar] = useState(0);
  const [noDibujadoListo, setNoDibujadoListo] = useState(false);
  const [conRevisionFinal, setConRevisionFinal] = useState(false);
  const [fallidos, setFallidos] = useState(0);
  const [resultado, setResultado] = useState<{ agregados: number; conIa: number; fallidos: number } | null>(null);
  const detener = useRef(false);
  // Las props de la última pantalla: al contestar las preguntas pendientes cambian los candidatos.
  const ultimas = useRef(p);
  ultimas.current = p;
  const total = useRef(0);
  // Cronómetro de la corrida (provisional: se ve el tiempo mientras se decide si se queda).
  const inicio = useRef<number | null>(null);
  const [segundos, setSegundos] = useState(0);
  const [duracion, setDuracion] = useState<number | null>(null);
  const [costo, setCosto] = useState<{ usd: number; llamadas: number } | null>(null);
  const [sinSaldo, setSinSaldo] = useState<string | null>(null);
  const [revisandoSaldo, setRevisandoSaldo] = useState(false);

  useEffect(() => {
    if (!p.abierto) return;
    const b = p.borrador.leer();
    setBorradorPrevio(b);
    setPaso(b && b.filas.length > 0 ? 'borrador' : 'antes');
    setFilas([]);
    setHechos(0);
    setFallidos(0);
    setResultado(null);
    setCosto(null);
    setDuracion(null);
    setSegundos(0);
    setSinSaldo(null);
    inicio.current = null;
  }, [p.abierto]);

  useEffect(() => {
    if (paso !== 'agregando') return;
    const t = setInterval(() => setSegundosAgregar(Math.round((Date.now() - (inicioAgregar.current ?? Date.now())) / 1000)), 1000);
    return () => clearInterval(t);
  }, [paso]);

  useEffect(() => {
    if (paso !== 'armando') return;
    const t = setInterval(() => setSegundos(Math.round((Date.now() - (inicio.current ?? Date.now())) / 1000)), 1000);
    return () => clearInterval(t);
  }, [paso]);

  // Borrador: lo que se está revisando queda guardado (por plano) para retomarlo después.
  useEffect(() => {
    if (paso !== 'revision' || filas.length === 0) return;
    const t = setTimeout(() => {
      p.borrador.guardar({
        fecha: new Date().toISOString(),
        op,
        filas: filas.map(({ cantidadStr: _c, midiendo: _m, ...x }) => x),
        cantidades: Object.fromEntries(filas.map((f) => [f.key, f.cantidadStr])),
        duracion,
        costo,
      });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, filas, costo]);

  const cargarBorrador = (b: BorradorArmado) => {
    setOp(b.op);
    // Un borrador de antes de «un renglón por concepto» se junta al abrirlo (sin volver a consultar nada).
    const yaJuntado = b.filas.some((x) => x.elementos != null);
    const filasB = yaJuntado ? b.filas : agruparPorConcepto(b.filas);
    setFilas(
      filasB.map((x) => ({
        ...x,
        cantidadStr:
          (yaJuntado ? b.cantidades[x.key] : undefined) ?? (x.cantidad.cantidad != null ? fmtCant(x.cantidad.cantidad) : ''),
        midiendo: false,
      })),
    );
    setDuracion(b.duracion);
    setCosto(b.costo ?? null);
    setPaso('revision');
  };

  // Staff: el costo se pide al terminar (y otra vez al final: medir también consulta a la IA). El
  // registro del consumo es asíncrono: se espera un momento antes de leerlo.
  const leerCosto = () => {
    const desde = inicio.current;
    if (!p.costoStaff || desde == null) return;
    const pedir = p.costoStaff;
    setTimeout(() => {
      void pedir(new Date(desde - 1000).toISOString())
        .then(setCosto)
        .catch(() => undefined);
    }, 1500);
  };

  const conCantidad = (x: PropuestaArmado): Fila => ({
    ...x,
    cantidadStr: x.cantidad.cantidad != null ? fmtCant(x.cantidad.cantidad) : '',
    midiendo: false,
  });

  const buscarAPedido = async (f: Fila) => {
    if (!f.consulta) return;
    setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, midiendo: true } : x)));
    try {
      const r = await p.buscarOpciones(f.consulta);
      const i = r.opciones.findIndex((o) => sirveDelCatalogo(o, !r.noEsExacto));
      setFilas((prev) =>
        prev.map((x) =>
          x.key === f.key
            ? {
                ...x,
                opciones: r.opciones,
                eleccion: i >= 0 ? i : 'ia',
                consulta: undefined,
                midiendo: false,
                nota: [x.nota, i >= 0 && r.noEsExacto ? 'Va lo más parecido de tu catálogo: revísalo.' : null, i < 0 ? 'Tus catálogos no tienen nada parecido.' : null]
                  .filter(Boolean)
                  .join(' '),
              }
            : x,
        ),
      );
    } catch (e) {
      if ((e as Error).message === SIN_SALDO) setSinSaldo('Ya no te quedan créditos este mes: no se pudo buscar.');
      setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, midiendo: false } : x)));
    }
  };

  const imprimir = () => {
    const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
    const filasHtml = filas
      .filter((f) => f.eleccion !== 'fuera' && !sinPadreDe(f))
      .map((f) => {
        const o = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : undefined;
        const cant = numero(f.cantidadStr);
        const concepto = f.eleccion === 'ia' ? `Generar con IA: ${f.sintetico.descripcion}` : `${o?.clave ?? ''} · ${o?.descripcion ?? ''}`;
        const unidad = f.eleccion === 'ia' ? f.sintetico.unidad : o?.unidad ?? f.sintetico.unidad;
        // Lo que hay que revisar sale en amarillo con su porqué: para eso se imprime.
        const avisos = [cant == null ? 'Sin cantidad: entra con 0, complétala.' : null, f.nota, f.cantidad.nota].filter((x): x is string => !!x);
        const marcar = f.cantidad.revisar || cant == null || avisos.length > 0;
        const clase = cant == null ? 'sincant' : marcar ? 'revisar' : '';
        const notas = avisos.length ? `<div class="aviso">⚠ ${avisos.map(esc).join(' ')}</div>` : '';
        return `<tr${clase ? ` class="${clase}"` : ''}><td${f.tipo === 'complemento' ? ' style="padding-left:18px"' : ''}>${f.tipo === 'complemento' ? '↳ ' : ''}${esc(f.nombre)}</td><td>${esc(concepto)}${notas}</td><td class="n">${cant != null ? esc(fmtCant(cant)) : '—'} ${esc(unidad ?? '')}</td><td class="n">${o?.precio != null ? esc(pesos(o.precio)) : f.eleccion === 'ia' ? 'con IA' : '—'}</td><td class="n">${o?.precio != null && cant != null ? esc(pesos(o.precio * cant)) : '—'}</td></tr>`;
      })
      .join('');
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>Borrador · ${esc(p.tituloImpresion)}</title><style>body{font:12px system-ui,sans-serif;margin:24px;color:#111}h1{font-size:16px;margin:0 0 4px}p{margin:0 0 12px;color:#555}table{width:100%;border-collapse:collapse}th,td{border-bottom:1px solid #ddd;padding:4px 6px;vertical-align:top;text-align:left}th{background:#f3f3f3}.n{text-align:right;white-space:nowrap}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}tr.revisar td{background:#fff4c2}tr.sincant td{background:#ffd9d9}.aviso{margin-top:3px;font-size:11px;color:#7a5a00}.leyenda{display:inline-block;background:#fff4c2;padding:1px 6px;border-radius:3px}</style></head><body><h1>Borrador del presupuesto · ${esc(p.tituloImpresion)}</h1><p>${esc(new Date().toLocaleString('es-MX'))} · Total de lo que tiene precio: ${esc(pesos(cuentas.importe))} · ${cuentas.ia} para generar con IA (sin precio todavía)</p><p><span class="leyenda" style="background:#ffd9d9">En rojo</span>: sin cantidad (entra con 0). <span class="leyenda">En amarillo</span>: lo que hay que revisar antes de usar (concepto parecido pero no igual, cantidad supuesta o sin cantidad), con el porqué debajo.</p><table><thead><tr><th>Del plano</th><th>Concepto</th><th class="n">Cantidad</th><th class="n">P. unitario</th><th class="n">Importe</th></tr></thead><tbody>${filasHtml}</tbody></table></body></html>`,
    );
    w.document.close();
    w.focus();
    w.print();
  };

  // Quitar un renglón (con confirmación) y volver a agregarlo con lo que tenía.
  const [confirmarQuitar, setConfirmarQuitar] = useState<string | null>(null);
  const quitar = (f: Fila) => {
    fijar(f.key);
    setFilas((prev) =>
      prev.map((x) =>
        x.key === f.key ? { ...x, eleccionPrevia: x.eleccion, cantidadPrevia: x.cantidadStr, eleccion: 'fuera', quitadoPorTi: true } : x,
      ),
    );
    setConfirmarQuitar(null);
  };
  // Restablecer devuelve EXACTAMENTE lo que tenía (concepto y cantidad), sin volver a medir: medir de nuevo
  // consulta a la IA la magnitud del concepto y, si esa consulta no decide, la cantidad quedaba en 0.
  const volverAAgregar = (f: Fila) => {
    fijar(f.key);
    const previa = f.eleccionPrevia != null && f.eleccionPrevia !== 'fuera' ? f.eleccionPrevia : null;
    if (previa != null) {
      setFilas((prev) =>
        prev.map((x) =>
          x.key === f.key
            ? { ...x, eleccion: previa, cantidadStr: x.cantidadPrevia ?? x.cantidadStr, quitadoPorTi: false, eleccionPrevia: undefined, cantidadPrevia: undefined }
            : x,
        ),
      );
      return;
    }
    // Sin nada que restablecer (lo había quitado el asistente): el primero con precio, o a IA.
    const i = f.opciones.findIndex((o) => o.precio != null && o.precio > 0);
    void elegir(f, i >= 0 ? i : 'ia');
  };

  const elegir = async (f: Fila, e: Eleccion | 'buscar') => {
    if (e === 'buscar') return void buscarAPedido(f);
    // Solo el principal se vuelve a medir con la unidad del concepto nuevo.
    if (typeof e !== 'number' || f.tipo !== 'principal' || !f.elementoKey) {
      setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, eleccion: e, quitadoPorTi: e === 'fuera' } : x)));
      return;
    }
    const opcion = f.opciones[e];
    if (!opcion) return;
    const elementoKey = f.elementoKey;
    setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, eleccion: e, quitadoPorTi: false, midiendo: true } : x)));
    let cant: CantidadArmado;
    try {
      const elementos = f.elementos && f.elementos.length > 1 ? f.elementos : [elementoKey];
      const medidas = await Promise.all(elementos.map((k) => p.medir(k, opcion)));
      cant = {
        cantidad: sumar(medidas.map((m) => m.cantidad)),
        revisar: medidas.some((m) => m.revisar),
        nota: medidas.find((m) => m.nota)?.nota ?? null,
        foto: juntarTodas(medidas.map((m) => m.foto)),
      };
    } catch {
      cant = { cantidad: null, revisar: true, nota: 'No se pudo medir con la unidad de este concepto: escribe la cantidad.' };
    }
    // Si la medición nueva no da cantidad pero el concepto nuevo va en la MISMA unidad que el anterior, se
    // conserva la que tenía (la medida del elemento no cambió): no se pierde por un tropiezo de la medición.
    const anterior = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : undefined;
    const u = (x: string | null | undefined) => (x ?? '').toLowerCase().replace(/²/g, '2').replace(/³/g, '3').replace(/[\s.]/g, '');
    const conservar = cant.cantidad == null && !!f.cantidadStr && !!anterior && u(anterior.unidad) === u(opcion.unidad);
    setFilas((prev) =>
      prev.map((x) =>
        x.key === f.key && x.eleccion === e
          ? conservar
            ? { ...x, midiendo: false }
            : { ...x, cantidad: cant, cantidadStr: cant.cantidad != null ? fmtCant(cant.cantidad) : '', midiendo: false }
          : x,
      ),
    );
  };

  const armar = async () => {
    setRevisandoSaldo(true);
    // Lo que no contestó va con la respuesta recomendada: se pinta como elegida y así se lee.
    const sinContestar = p.previo.preguntas.map((q) => q.key);
    for (const q of p.previo.preguntas) {
      if (q.entrada?.valor.trim() && q.entrada.confirmar) q.entrada.confirmar();
      else q.opciones.find((o) => o.recomendada)?.elegir();
    }
    const saldo = await p.revisarSaldo().catch(() => ({ alcanza: true, aviso: null }));
    // Esperar a que esas respuestas lleguen a los candidatos (unas se guardan en el servidor).
    for (let i = 0; i < 40 && ultimas.current.previo.preguntas.some((q) => sinContestar.includes(q.key) && q.opciones.some((o) => o.recomendada)); i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
    const pa = ultimas.current;
    setRevisandoSaldo(false);
    if (!saldo.alcanza) {
      setSinSaldo(saldo.aviso ?? 'Ya no te quedan créditos este mes: no se puede buscar el concepto de cada elemento.');
      return;
    }
    setSinSaldo(null);
    detener.current = false;
    total.current = pa.candidatos.length + (op.noDibujado && pa.previo.puedeNoDibujado ? 1 : 0);
    inicio.current = Date.now();
    setSegundos(0);
    setHechos(0);
    setFallidos(0);
    setEtapa('buscando');
    setNoDibujadoListo(false);
    setConRevisionFinal(!!pa.revisar);
    setPaso('armando');
    const cola = [...pa.candidatos];
    const listas: PropuestaArmado[] = [];
    const extras: PropuestaArmado[] = [];
    const trabajador = async () => {
      for (;;) {
        if (detener.current) return;
        const c = cola.shift();
        if (!c) return;
        try {
          listas.push(...(await pa.proponer(c, op)));
        } catch (e) {
          if ((e as Error).message === SIN_SALDO) {
            detener.current = true;
            setSinSaldo('Se acabaron los créditos del mes a la mitad: se detuvo. Lo que se armó hasta ahí está abajo.');
            return;
          }
          setFallidos((n) => n + 1);
        }
        setHechos((n) => n + 1);
      }
    };
    const noDibujado = async () => {
      if (!op.noDibujado || !pa.previo.puedeNoDibujado) return;
      try {
        extras.push(...(await pa.proponerNoDibujado(op)));
      } catch (e) {
        if ((e as Error).message === SIN_SALDO) {
          detener.current = true;
          setSinSaldo('Se acabaron los créditos del mes a la mitad: se detuvo. Lo que se armó hasta ahí está abajo.');
          return;
        }
        setFallidos((n) => n + 1);
      }
      setNoDibujadoListo(true);
      setHechos((n) => n + 1);
    };
    await Promise.all([noDibujado(), ...Array.from({ length: EN_PARALELO }, trabajador)]);
    // En el orden en que se propusieron (de lo más grande a lo más chico), cada principal seguido de sus
    // complementarios; lo que no trae el plano, al final.
    const orden = new Map(pa.candidatos.map((c, i) => [c.key, i]));
    const pos = (x: PropuestaArmado) => orden.get(x.elementoKey ?? '') ?? 0;
    listas.sort((a, b) => pos(a) - pos(b) || (a.tipo === 'principal' ? -1 : b.tipo === 'principal' ? 1 : 0));
    // Revisión final, como la de un ingeniero antes de entregar: si falla, la hoja va como se armó.
    const armadas = [...listas, ...extras];
    if (pa.revisar && !detener.current) setEtapa('revisando');
    const revisadas = pa.revisar && !detener.current ? await pa.revisar(armadas).catch(() => armadas) : armadas;
    setFilas(agruparPorConcepto(revisadas).map(conCantidad));
    setDuracion(Math.round((Date.now() - (inicio.current ?? Date.now())) / 1000));
    setPaso('revision');
    leerCosto();
    p.alTerminar();
  };

  // Un complementario cuyo principal no va a la hoja no puede entrar (cuelga de él).
  const fuera = useMemo(() => new Set(filas.filter((f) => f.eleccion === 'fuera').map((f) => f.key)), [filas]);
  const sinPadreDe = (f: Fila) => {
    const ps = f.padres?.length ? f.padres : f.padre ? [f.padre] : [];
    return ps.length > 0 && ps.every((x) => fuera.has(x));
  };
  // Lo sin cantidad también entra: con 0 y marcado para revisar, para completarlo en la hoja.
  const entra = (f: Fila) => f.eleccion !== 'fuera' && !sinPadreDe(f) && !f.midiendo;
  const sinCantidadDe = (f: Fila) => entra(f) && numero(f.cantidadStr) == null;
  const porRevisarDe = (f: Fila) => entra(f) && (f.cantidad.revisar || f.cantidad.nota != null || numero(f.cantidadStr) == null);
  /** Por qué un renglón no entra a la hoja, para que se entienda sin adivinar. */
  const motivoFuera = (f: Fila): string | null => {
    if (sinPadreDe(f)) return 'No se agrega porque su concepto principal no se agrega.';
    if (f.eleccion !== 'fuera') return null;
    if (f.quitadoPorTi) return 'Quitado por ti: no entra a la hoja. Con «Restablecer» vuelve.';
    if (f.nota?.startsWith('Parece que no se construye')) return `No se agrega. ${f.nota}`;
    if (f.tipo === 'complemento' && f.nota?.includes('Depende de la obra'))
      return 'No se agrega porque no siempre aplica en una obra así. Si va en la tuya, elige su concepto.';
    if (f.tipo === 'complemento' && f.nota?.includes('Tus catálogos no lo tienen'))
      return 'No se agrega: tus catálogos no lo tienen. Si va, búscalo o elige «Generar con IA» (consume de tu bolsa).';
    return 'No se agrega. Si va, elige su concepto.';
  };
  const [filtro, setFiltroBase] = useState<'todos' | 'agregan' | 'revisar' | 'sinCantidad' | 'fuera' | 'quitados'>('todos');
  // Lo que se cambia dentro de un filtro se queda a la vista hasta cambiar de filtro: si no, desaparecía
  // al dejar de cumplirlo y parecía que el cambio no se había hecho.
  const [fijos, setFijos] = useState<ReadonlySet<string>>(new Set());
  const setFiltro = (k: typeof filtro) => {
    setFiltroBase(k);
    setFijos(new Set());
  };
  const fijar = (key: string) => setFijos((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
  // «Buscar otro concepto…» abierto en una fila. Se comporta como el «Buscar» del panel de ci: busca
  // mientras se escribe (350 ms), exige todas las palabras y muestra la lista para que elijas tú.
  const [busqueda, setBusqueda] = useState<{ key: string; texto: string; buscando: boolean; resultados: OpcionArmado[] | null } | null>(null);
  useEffect(() => {
    if (!busqueda) return;
    const q = busqueda.texto.trim();
    if (q.length < 2) return;
    const { key } = busqueda;
    const t = setTimeout(() => {
      setBusqueda((b) => (b && b.key === key ? { ...b, buscando: true } : b));
      void p
        .buscarTexto(q)
        .catch(() => [] as OpcionArmado[])
        .then((r) => setBusqueda((b) => (b && b.key === key && b.texto.trim() === q ? { ...b, buscando: false, resultados: r } : b)));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busqueda?.key, busqueda?.texto]);
  const elegirEncontrado = (f: Fila, o: OpcionArmado) => {
    const opciones = [o, ...f.opciones.filter((x) => x.resourceId !== o.resourceId)];
    setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, opciones, consulta: undefined } : x)));
    setBusqueda(null);
    void elegir({ ...f, opciones }, 0);
  };


  const cuentas = useMemo(() => {
    let catalogo = 0;
    let ia = 0;
    let importe = 0;
    let sinCantidad = 0;
    let complementos = 0;
    let noDibujado = 0;
    for (const f of filas) {
      if (f.eleccion === 'fuera' || sinPadreDe(f)) continue;
      const cant = numero(f.cantidadStr);
      // Sin cantidad entra igual, con 0 (lo dice el resumen y la fila sale en rojo).
      if (cant == null) sinCantidad++;
      if (f.eleccion === 'ia') ia++;
      else {
        catalogo++;
        const pu = f.opciones[f.eleccion]?.precio;
        if (pu != null && cant != null) importe += cant * pu;
      }
      if (f.tipo === 'complemento') complementos++;
      if (f.tipo === 'noDibujado') noDibujado++;
    }
    const principales = catalogo + ia - complementos - noDibujado;
    return { catalogo, ia, importe, sinCantidad, complementos, noDibujado, principales };
  }, [filas, fuera]);

  const agregar = async () => {
    const lista: FilaParaAgregar[] = [];
    for (const f of filas) {
      if (!entra(f)) continue;
      // Sin tocar, entra lo medido tal cual (no el número redondeado de la tabla): así cuadra con su generador.
      const medido = f.cantidad.cantidad;
      const editada = medido == null || f.cantidadStr !== fmtCant(medido);
      const cantidad = !editada ? medido! : numero(f.cantidadStr) ?? 0;
      const base = {
        key: f.key, tipo: f.tipo, elementoKey: f.elementoKey, elementos: f.elementos, padre: f.padre, padres: f.padres, cantidad,
        partida: f.partida, revisar: f.cantidad.revisar || cantidad === 0,
        foto: f.cantidad.foto ?? null, cantidadEscritaAMano: editada,
        ...(f.deTipos ? { deTipos: f.deTipos } : {}),
      };
      if (f.eleccion === 'ia') lista.push({ ...base, opcion: null, sintetico: f.sintetico });
      else if (typeof f.eleccion === 'number') lista.push({ ...base, opcion: f.opciones[f.eleccion] ?? null, sintetico: null });
    }
    inicioAgregar.current = Date.now();
    setSegundosAgregar(0);
    setAvanceAgregar({ n: 0, total: lista.length, etapa: 'agregando' });
    setPaso('agregando');
    setErrorAgregar(null);
    let r: Awaited<ReturnType<typeof p.agregar>>;
    try {
      r = await p.agregar(lista, (n, total, etapa) => setAvanceAgregar({ n, total, etapa }));
    } catch (e) {
      // Al rehacer, el reemplazo es todo o nada: si falla, la hoja anterior sigue igual y el borrador también.
      setErrorAgregar(
        (e as Error).message ||
          (p.rehacer ? 'No se pudo reemplazar la hoja. No se cambió nada: la hoja anterior sigue igual.' : 'No se pudo agregar a la hoja.'),
      );
      setPaso('revision');
      return;
    }
    setResultado(r);
    if (r.agregados > 0) p.borrador.borrar();
    setPaso('listo');
    leerCosto();
    p.alTerminar();
  };

  const pv = p.previo;
  const bloqueado = pv.catalogos.length === 0 || pv.escala != null || p.candidatos.length === 0;
  // Mientras se revisa el catálogo para saber qué preguntar, «Armar» espera: si se pudiera pulsar, las preguntas aparecerían
  // cuando ya no sirven.
  const esperandoMaterial = pv.revisandoMaterial === true;
  const capas = p.candidatos.filter((c) => c.tipo === 'capa').length;
  const bloques = p.candidatos.length - capas;

  const filaTabla = (f: Fila) => {
    const opc = typeof f.eleccion === 'number' ? f.opciones[f.eleccion] : undefined;
    const cant = numero(f.cantidadStr);
    const unidad = f.eleccion === 'ia' ? f.sintetico.unidad : opc?.unidad ?? f.sintetico.unidad;
    const sinPadre = sinPadreDe(f);
    const sinCant = sinCantidadDe(f);
    const motivo = motivoFuera(f);
    const tachado = !!f.quitadoPorTi && f.eleccion === 'fuera';
    const estiloTachado = tachado ? { textDecoration: 'line-through', color: 'var(--text-tertiary)' } : {};
    const texto =
      f.eleccion === 'fuera' ? 'No agregar' : f.eleccion === 'ia' ? `Generar con IA: ${f.sintetico.descripcion}` : `${opc?.clave} · ${opc?.descripcion}`;
    return (
      <tr key={f.key} style={{ background: sinCant ? 'var(--bad-weak)' : undefined }}>
        <td className={s.td} style={f.tipo === 'complemento' ? { paddingLeft: 24 } : undefined}>
          <div style={{ fontWeight: f.tipo === 'principal' ? 600 : 400, opacity: motivo ? 0.6 : 1, ...estiloTachado }}>
            {f.tipo === 'complemento' ? '↳ ' : ''}
            {f.nombre}
          </div>
          {f.medida && <div className={s.sub}>{f.medida}</div>}
        </td>
        <td className={s.td}>
          {tachado ? (
            <div style={{ ...estiloTachado, fontSize: 12, padding: '4px 0' }}>
              {(() => {
                const previa = f.eleccionPrevia;
                const o = typeof previa === 'number' ? f.opciones[previa] : undefined;
                return o ? `${o.clave} · ${o.descripcion}` : previa === 'ia' ? `Generar con IA: ${f.sintetico.descripcion}` : f.sintetico.descripcion;
              })()}
            </div>
          ) : (
          <Dropdown
            size="small"
            appearance="outline"
            style={{ width: '100%', minWidth: 0, borderColor: 'var(--border-strong)' }}
            expandIcon={<ChevronDown20Regular style={{ color: 'var(--accent)' }} />}
            value={texto}
            selectedOptions={[String(f.eleccion)]}
            disabled={sinPadre}
            aria-label={`Concepto para ${f.nombre}. Cámbialo aquí`}
            title="Cambiar el concepto"
            onOptionSelect={(_e, d) => {
              const v = d.optionValue ?? '';
              fijar(f.key);
              if (v === 'texto') return setBusqueda({ key: f.key, texto: f.nombre, buscando: false, resultados: null });
              void elegir(f, v === 'ia' || v === 'fuera' || v === 'buscar' ? v : Number(v));
            }}
          >
            <Option value="texto" text="Buscar otro concepto…">
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontWeight: 600 }}>
                <Search16Regular /> Buscar otro concepto…
              </span>
            </Option>
            {f.consulta && f.opciones.length === 0 && (
              <Option value="buscar" text="Recomendados para este renglón">
                Recomendados para este renglón
              </Option>
            )}
            {f.opciones.map((o, i) => (
              <Option key={o.resourceId} value={String(i)} text={`${o.clave} · ${o.descripcion}`}>
                <div>
                  <div>
                    {o.clave} · {o.descripcion}
                  </div>
                  <div className={s.sub}>
                    {o.unidad ?? 's/u'} · {o.precio != null ? pesos(o.precio) : 's/precio'}
                    {o.razon ? ` · ${o.razon}` : ''}
                  </div>
                </div>
              </Option>
            ))}
            <Option value="ia" text={`Generar con IA: ${f.sintetico.descripcion}`}>
              Generar con IA: {f.sintetico.descripcion}
            </Option>
            <Option value="fuera" text="No agregar">
              No agregar
            </Option>
          </Dropdown>
          )}
          {busqueda?.key === f.key && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Input
                  size="small"
                  style={{ flex: 1, minWidth: 0 }}
                  autoFocus
                  contentBefore={<Search16Regular />}
                  value={busqueda.texto}
                  placeholder="Busca por clave o descripción en tus catálogos"
                  aria-label={`Buscar otro concepto para ${f.nombre}`}
                  onChange={(_e, d) => setBusqueda({ ...busqueda, texto: d.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setBusqueda(null);
                  }}
                />
                {busqueda.buscando && <Spinner size="extra-tiny" />}
                <Button size="small" appearance="secondary" onClick={() => setBusqueda(null)}>
                  Cerrar
                </Button>
              </span>
              {busqueda.resultados && busqueda.resultados.length > 0 && (
                <div
                  role="listbox"
                  aria-label="Conceptos encontrados"
                  style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6 }}
                >
                  {busqueda.resultados.map((o) => (
                    <button
                      key={o.resourceId}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => elegirEncontrado(f, o)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '5px 8px',
                        border: 0,
                        borderBottom: '1px solid var(--border-subtle)',
                        background: 'var(--bg-surface)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: 12,
                      }}
                    >
                      <b>{o.clave}</b> · {o.descripcion}
                      <span className={s.sub}>
                        {' '}
                        — {o.unidad ?? 's/u'} · {o.precio != null ? pesos(o.precio) : 's/precio'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {busqueda.resultados && busqueda.resultados.length === 0 && !busqueda.buscando && (
                <div className={s.sub}>No está en tus catálogos con esas palabras. Quita una o prueba otras.</div>
              )}
            </div>
          )}
          {motivo ? (
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{motivo}</div>
          ) : (
            <>
              {sinCant && (
                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--bad)', fontWeight: 600 }}>
                  Sin cantidad: el plano no la da. Entra con 0 y marcado para que la escribas (aquí o en la hoja).
                </div>
              )}
              {f.nota && (
                // Concepto parecido pero no igual: el porqué en amarillo, como en la impresión.
                <div className={s.sub} style={{ marginTop: 4, ...(f.cantidad.revisar ? { color: 'var(--warn)' } : {}) }}>
                  {f.nota}
                </div>
              )}
              {f.eleccion !== 'fuera' && f.cantidad.nota && !sinCant && (
                <div className={s.sub} style={{ marginTop: 4, color: 'var(--warn)' }}>
                  {f.cantidad.nota}
                </div>
              )}
            </>
          )}
        </td>
        <td className={s.num}>
          {f.midiendo ? (
            <Spinner size="extra-tiny" />
          ) : motivo ? (
            // No se agrega, pero se ve qué cantidad llevaría si lo cambias.
            <span style={{ color: 'var(--text-tertiary)' }} title="No se agrega; esta es la cantidad que llevaría">
              {f.cantidadStr || '0'} {unidad ?? ''}
            </span>
          ) : (
            <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
              <Input
                size="small"
                style={{ width: 90, ...(sinCant ? { borderColor: 'var(--bad)' } : {}) }}
                placeholder="0"
                value={f.cantidadStr}
                aria-label={`Cantidad para ${f.nombre}`}
                onChange={(_e, d) => {
                  fijar(f.key);
                  setFilas((prev) => prev.map((x) => (x.key === f.key ? { ...x, cantidadStr: d.value } : x)));
                }}
              />
              {unidad ?? ''}
            </span>
          )}
        </td>
        <td className={s.num}>{opc?.precio != null ? pesos(opc.precio) : f.eleccion === 'ia' ? 'con IA' : '—'}</td>
        <td className={s.num}>{opc?.precio != null && cant != null && !sinPadre ? pesos(opc.precio * cant) : '—'}</td>
        <td className={s.td} style={{ width: '1%', whiteSpace: 'nowrap', paddingLeft: 4, paddingRight: 6 }}>
          {sinPadre ? null : f.eleccion === 'fuera' ? (
            <Button
              size="small"
              appearance="secondary"
              icon={<ArrowUndo16Regular />}
              style={{ minWidth: 118 }}
              aria-label={`Restablecer ${f.nombre}`}
              onClick={() => volverAAgregar(f)}
            >
              Restablecer
            </Button>
          ) : (
            <Popover
              open={confirmarQuitar === f.key}
              onOpenChange={(_e, d) => setConfirmarQuitar(d.open ? f.key : null)}
              withArrow
              positioning="before"
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button size="small" appearance="secondary" icon={<Delete16Regular />} style={{ minWidth: 118 }} aria-label={`Quitar ${f.nombre}`}>
                  Quitar
                </Button>
              </PopoverTrigger>
              <PopoverSurface style={{ maxWidth: 300 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>¿Quitar este concepto?</div>
                <div className={s.sub} style={{ marginBottom: 8 }}>
                  No entra a la hoja; se queda tachado en la lista.
                  {f.tipo === 'principal' && (() => {
                    const n = filas.filter((x) => x.tipo === 'complemento' && x.eleccion !== 'fuera' && (x.padres?.length ? x.padres.every((k) => k === f.key || fuera.has(k)) : x.padre === f.key)).length;
                    return n > 0 ? ` Tampoco ${n === 1 ? 'su complementario' : `sus ${n} complementarios`}.` : '';
                  })()}{' '}
                  Con «Restablecer» vuelve.
                </div>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Button size="small" appearance="secondary" onClick={() => setConfirmarQuitar(null)}>
                    Cancelar
                  </Button>
                  <Button size="small" appearance="primary" onClick={() => quitar(f)}>
                    Quitar
                  </Button>
                </span>
              </PopoverSurface>
            </Popover>
          )}
        </td>
      </tr>
    );
  };

  const contenido = (() => {
    if (paso === 'borrador' && borradorPrevio)
      return (
        <div className={s.bloque}>
          <Punto tono="info">
            Tienes un borrador de este plano del <b>{new Date(borradorPrevio.fecha).toLocaleString('es-MX')}</b> con{' '}
            {plural(borradorPrevio.filas.length, 'renglón', 'renglones')} todavía sin agregar a la hoja.
          </Punto>
          <Punto tono="info">Seguir con él no gasta nada. Empezar de cero vuelve a buscar todo (consume de tu bolsa) y descarta el borrador.</Punto>
        </div>
      );
    if (paso === 'antes')
      return (
        <div className={s.bloque}>
          {pv.chequeo && (
            <Punto tono={pv.chequeo.veredicto === 'bien' ? 'ok' : 'mal'}>
              <b>{TITULO_VEREDICTO_CHEQUEO[pv.chequeo.veredicto]}.</b>{' '}
              {pv.chequeo.puntos.filter((x) => x.tono === 'mal').map((x) => x.texto).join(' ')}
            </Punto>
          )}
          {pv.catalogos.length === 0 ? (
            <Punto tono="mal">
              <b>Falta elegir tus catálogos de referencia</b> (panel de la izquierda): de ahí salen los conceptos y sus precios.
            </Punto>
          ) : (
            <Punto tono="ok">Los conceptos se buscan en: {pv.catalogos.join(', ')}.</Punto>
          )}
          {pv.escala && (
            <Punto tono="mal">
              <b>¿En qué unidad está dibujado este plano?</b> Sin eso no se puede medir nada.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                {pv.escala.opciones.map((o) => (
                  <div key={o.unidad} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Button size="small" onClick={() => void pv.escala!.elegir(o.unidad)}>
                      En {o.nombre}
                    </Button>
                    <span className={s.sub}>{o.consecuencia}</span>
                  </div>
                ))}
              </div>
            </Punto>
          )}
          {pv.altura && (
            <Punto tono={numero(pv.altura.valor) ? 'ok' : 'mal'}>
              <b>¿Qué alto tienen los muros?</b> (para sacar su área){' '}
              <Input
                size="small"
                style={{ width: 90 }}
                value={pv.altura.valor}
                onChange={(_e, d) => pv.altura!.cambiar(d.value)}
                placeholder="ej. 2.50"
                aria-label="Alto de los muros en metros"
              />{' '}
              m
              <div className={s.sub}>
                {numero(pv.altura.valor)
                  ? pv.altura.deDonde ?? 'Lo escribiste tú.'
                  : 'El plano no trae una fachada o un corte con su altura. Sin ella, los conceptos de muro por m² quedan sin cantidad y los tendrás que escribir.'}
              </div>
            </Punto>
          )}
          <Punto tono="info">
            {p.candidatos.length === 0 ? (
              <>
                <b>No hay nada que armar:</b> todo lo que se construye en este plano ya tiene su concepto en la hoja. Para
                armarlo de nuevo, primero quita de la hoja los renglones de este plano.
              </>
            ) : (
              <>
                Se va a buscar el concepto de{' '}
                <b>
                  {pv.esIfc
                    ? plural(p.candidatos.length, 'tipo de elemento del modelo', 'tipos de elemento del modelo')
                    : [capas ? plural(capas, 'capa', 'capas') : null, bloques ? plural(bloques, 'bloque', 'bloques') : null].filter(Boolean).join(' y ')}
                </b>{' '}
                {p.rehacer
                  ? 'que se construyen, del más grande al más chico: se arma toda la hoja de nuevo. Tu hoja actual sigue igual hasta que pulses «Reemplazar la hoja» al final.'
                  : 'que se construyen y todavía no están en la hoja, del más grande al más chico.'}
                {!p.rehacer && pv.yaConConcepto > 0 && ` ${plural(pv.yaConConcepto, 'ya tiene', 'ya tienen')} concepto y no se tocan.`}
                <details style={{ marginTop: 4 }}>
                  <summary className={s.sub} style={{ cursor: 'pointer' }}>
                    Ver cuáles
                  </summary>
                  <div className={s.sub} style={{ columns: 2, marginTop: 4 }}>
                    {p.candidatos.map((c) => (
                      <div key={c.key}>
                        {c.nombre}
                        {c.medida ? ` · ${c.medida}` : ''}
                      </div>
                    ))}
                  </div>
                </details>
              </>
            )}
          </Punto>
          {pv.tipos && pv.tipos.total > 0 && (
            <Punto tono="info">
              <b>
                {pv.tipos.reconocidos} de {pv.tipos.total} {pv.tipos.total === 1 ? 'elemento ya tiene' : 'elementos ya tienen'} concepto
              </b>
              {pv.tipos.reconocidos > 0 ? ' (lo asignaste antes).' : '.'}
              {pv.tipos.sugeridos > 0 && ` ${plural(pv.tipos.sugeridos, 'se parece', 'se parecen')} a lo que presupuestaste antes: revísalos.`}
              {pv.tipos.conflictos > 0 && ` ${plural(pv.tipos.conflictos, 'tiene', 'tienen')} dos conceptos recordados: elige cuál vale.`}
              {pv.tipos.noVigentes > 0 && ` ${plural(pv.tipos.noVigentes, 'lleva', 'llevan')} un concepto que ya no sirve.`}{' '}
              <Button size="small" appearance="transparent" onClick={pv.tipos.abrir}>
                Asignar conceptos a elementos…
              </Button>
            </Punto>
          )}
          {esperandoMaterial && (
            <div className={s.bloque} style={{ gap: 8, flexDirection: 'row', alignItems: 'center' }} role="status" aria-live="polite">
              <Spinner size="small" />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <b style={{ fontSize: 13 }}>Revisando tu catálogo para saber qué preguntarte…</b>
                <span className={s.sub}>Toma unos segundos. El botón «Armar» se activa cuando termine, para que veas las preguntas antes de empezar.</span>
              </span>
            </div>
          )}
          {pv.preguntas.length > 0 && (
            <div className={s.bloque} style={{ gap: 6 }}>
              <b style={{ fontSize: 13 }}>Antes, {pv.preguntas.length === 1 ? 'una pregunta' : `${pv.preguntas.length} preguntas`} sobre el plano</b>
              {pv.preguntas.map((q) => (
                <div key={q.key} className={s.pregunta}>
                  <span style={{ fontSize: 13 }}>{q.titulo}</span>
                  <span className={s.sub}>{q.detalle}</span>
                  {q.entrada?.sugerencias && q.entrada.sugerencias.length > 0 && (
                    <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {q.entrada.sugerencias.map((t) => (
                        <Button
                          key={t}
                          size="small"
                          appearance={q.entrada!.valor.trim().toLowerCase() === t.toLowerCase() ? 'primary' : 'secondary'}
                          onClick={() => q.entrada!.cambiar(q.entrada!.valor.trim().toLowerCase() === t.toLowerCase() ? '' : t)}
                        >
                          {t}
                        </Button>
                      ))}
                    </span>
                  )}
                  {q.entrada && (
                    <Input
                      size="small"
                      style={{ marginTop: 4, maxWidth: 360 }}
                      value={q.entrada.valor}
                      placeholder={q.entrada.placeholder}
                      aria-label={q.titulo}
                      onChange={(_e, d) => q.entrada!.cambiar(d.value)}
                    />
                  )}
                  {q.entrada?.siNoContestas && <span className={s.sub}>{q.entrada.siNoContestas}</span>}
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    {q.opciones.map((o) => (
                      <Button key={o.texto} size="small" appearance={o.recomendada ? 'primary' : 'secondary'} onClick={o.elegir}>
                        {o.texto}
                      </Button>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          )}
          {pv.avisoMaterial && !esperandoMaterial && (
            <span className={s.sub} role="status">
              {pv.avisoMaterial}
            </span>
          )}
          <div className={s.pregunta}>
            <Checkbox
              checked={op.complementarios}
              onChange={(_e, d) => setOp((o) => ({ ...o, complementarios: !!d.checked }))}
              label={<b>¿También sus complementarios?</b>}
            />
            <span className={s.sub} style={{ paddingLeft: 30 }}>
              Lo que cada concepto implica y no está dibujado: excavación, dala y castillos de un muro, aplanado, cimbra… Cada uno cuelga de su concepto en la hoja.
            </span>
          </div>
          {pv.puedeNoDibujado && (
            <div className={s.pregunta}>
              <Checkbox
                checked={op.noDibujado}
                onChange={(_e, d) => setOp((o) => ({ ...o, noDibujado: !!d.checked }))}
                label={<b>¿También lo que no trae el plano?</b>}
              />
              <span className={s.sub} style={{ paddingLeft: 30 }}>
                Lo que la obra necesita y esta planta no representa —cimentación, losas, pisos, ventanas, instalaciones, trámites—, estimado con lo que el plano sí trae.
              </span>
            </div>
          )}
          <Punto tono="info">
            El concepto de cada elemento —y el de cada complementario y de lo que no trae el plano— se busca con «Recomendados» (IA): consume de tu bolsa de tokens del mes, más cuanto más pidas. Nada entra a la hoja hasta que lo revises.
          </Punto>
          <Punto tono="info">
            Lo que tus catálogos no tengan queda para <b>generar con IA</b>, pero <b>no se genera solo</b>: entra a la hoja marcado y sin precio, y al final te preguntamos si lo generas (es lo que más consume).
          </Punto>
          {sinSaldo && <Punto tono="mal">{sinSaldo}</Punto>}
        </div>
      );
    if (paso === 'armando')
      return (
        <div className={s.bloque} style={{ padding: '18px 0', gap: 8 }} role="status" aria-live="polite">
          {etapa === 'buscando' ? (
            <>
              <ProgressBar value={total.current ? hechos / total.current : 0} thickness="large" />
              <b style={{ fontSize: 13 }}>
                {conRevisionFinal ? 'Paso 1 de 2 · ' : ''}Buscando el concepto de cada elemento{op.complementarios ? ' y sus complementarios' : ''}
              </b>
              <div style={{ color: 'var(--text-secondary)' }}>
                {hechos} de {total.current} · {reloj(segundos)}
                {fallidos > 0 && ` · ${fallidos} sin respuesta`}
              </div>
              {op.noDibujado && (
                <div className={s.sub}>
                  {noDibujadoListo ? 'Lo que no trae el plano: listo.' : 'Lo que no trae el plano: pensando en lo que la obra necesita aunque no se dibuje…'}
                </div>
              )}
            </>
          ) : (
            <>
              <ProgressBar thickness="large" />
              <b style={{ fontSize: 13 }}>Paso 2 de 2 · Revisión final</b>
              <div style={{ color: 'var(--text-secondary)' }}>
                Ya se buscó todo. Ahora se revisa lo armado, como lo haría un ingeniero antes de entregar: que no sobre ni falte nada y que cada concepto tenga sentido. Puede tardar un par de minutos. · {reloj(segundos)}
              </div>
            </>
          )}
        </div>
      );
    if (paso === 'agregando')
      return (
        <div className={s.bloque} style={{ padding: '18px 0', gap: 8 }} role="status" aria-live="polite">
          <ProgressBar
            thickness="large"
            {...(avanceAgregar && avanceAgregar.etapa === 'agregando' && avanceAgregar.total > 0 && !p.rehacer
              ? { value: avanceAgregar.n / avanceAgregar.total }
              : {})}
          />
          <b style={{ fontSize: 13 }}>
            {avanceAgregar?.etapa === 'actualizando'
              ? 'Casi listo · Actualizando tu hoja…'
              : p.rehacer
                ? 'Reemplazando la hoja…'
                : `Agregando a la hoja… ${avanceAgregar?.n ?? 0} de ${avanceAgregar?.total ?? 0}`}
          </b>
          <div className={s.sub}>
            {avanceAgregar?.etapa === 'actualizando'
              ? 'Ya se guardó todo. Falta recargar la hoja con los precios y los totales.'
              : 'Cada concepto se guarda con su cantidad, su selección en el plano y su número generador. No cierres esta ventana.'}{' '}
            · {reloj(segundosAgregar)}
          </div>
        </div>
      );
    if (paso === 'listo' && resultado)
      return (
        <div className={s.bloque}>
          <Punto tono="ok">Se {resultado.agregados === 1 ? 'agregó 1 concepto' : `agregaron ${resultado.agregados} conceptos`} a la hoja.</Punto>
          {resultado.fallidos > 0 && (
            <Punto tono="mal">{plural(resultado.fallidos, 'no se pudo agregar', 'no se pudieron agregar')}: vuelve a intentarlo desde la lista.</Punto>
          )}
          {resultado.conIa > 0 ? (
            <Punto tono="info">
              <b>{plural(resultado.conIa, 'concepto necesita', 'conceptos necesitan')} IA</b> para su análisis de precio (tus catálogos no los tienen). Entraron marcados con IA. Generarlos toma un rato y consume de tu bolsa del mes; también puedes hacerlo después desde la hoja.
            </Punto>
          ) : (
            <Punto tono="info">Todo salió de tus catálogos: no hace falta generar nada con IA.</Punto>
          )}
          {costo && <LineaStaff costo={costo} />}
        </div>
      );
    // revisión
    // Lo que quitaste se sigue viendo (tachado) en «Todos» y está en «Quitados»; «No se agregan» es solo
    // lo que el asistente propuso no agregar.
    const quitado = (f: Fila) => !!f.quitadoPorTi && f.eleccion === 'fuera';
    const pasa = (f: Fila) =>
      filtro === 'todos'
        ? true
        : filtro === 'agregan'
          ? motivoFuera(f) == null && !quitado(f)
          : filtro === 'quitados'
            ? quitado(f)
            : filtro === 'revisar'
              ? porRevisarDe(f)
              : filtro === 'sinCantidad'
                ? sinCantidadDe(f)
                : motivoFuera(f) != null && !quitado(f);
    const visibles = filas.filter((f) => pasa(f) || fijos.has(f.key));
    // Los complementarios de lo que no trae el plano van en su sección, debajo de su renglón.
    const keysNoDib = new Set(filas.filter((f) => f.tipo === 'noDibujado').map((f) => f.key));
    const deNoDib = (f: Fila) => f.tipo === 'noDibujado' || (f.tipo === 'complemento' && keysNoDib.has(f.padre ?? ''));
    const principales = visibles.filter((f) => !deNoDib(f));
    const noDib = visibles.filter(deNoDib);
    const nQuitados = filas.filter((f) => f.quitadoPorTi && f.eleccion === 'fuera').length;
    const nFuera = filas.filter((f) => motivoFuera(f) != null && !(f.quitadoPorTi && f.eleccion === 'fuera')).length;
    const nRevisar = filas.filter(porRevisarDe).length;
    const nAgregan = cuentas.catalogo + cuentas.ia;
    const filtros: { k: typeof filtro; texto: string; n: number; ayuda: string }[] = [
      { k: 'todos', texto: 'Todas las propuestas', n: filas.length, ayuda: 'Todo lo que propuso el asistente: lo que se agrega más lo que no.' },
      { k: 'agregan', texto: 'Se agregan', n: nAgregan, ayuda: 'Lo que entra a tu hoja cuando pulses «Agregar a la hoja».' },
      { k: 'fuera', texto: 'No se agregan', n: nFuera, ayuda: 'Propuestas que el asistente dejó fuera; cada una dice por qué.' },
      { k: 'revisar', texto: 'Por revisar', n: nRevisar, ayuda: 'Las que conviene revisar antes de cotizar (medición dudosa o concepto parecido).' },
      { k: 'sinCantidad', texto: 'Sin cantidad', n: cuentas.sinCantidad, ayuda: 'El plano no dio la cantidad: entran con 0 para que la completes.' },
      { k: 'quitados', texto: 'Quitados por ti', n: nQuitados, ayuda: 'Lo que quitaste de la lista.' },
    ];
    return (
      <div className={s.bloque}>
        {p.rehacer && (
          <div style={{ fontSize: 13, lineHeight: 1.45, background: 'var(--bg-raised)', borderLeft: '3px solid var(--accent)', borderRadius: 6, padding: '8px 12px' }}>
            <b>Tu hoja actual ({p.rehacer.renglones === 1 ? '1 renglón' : `${p.rehacer.renglones} renglones`}) sigue igual.</b> Esta hoja nueva la
            reemplaza solo cuando pulses «Reemplazar la hoja»; si cierras o descartas, no cambia nada.
            {p.rehacer.pierde.length > 0 && (
              <div style={{ marginTop: 4, color: 'var(--warn)' }}>
                Al reemplazarla se pierde: {p.rehacer.pierde.join('; ')}.
              </div>
            )}
          </div>
        )}
        <div className={s.resumen}>
          <span className={s.total}>{pesos(cuentas.importe)}</span>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <b>
              {duracion != null && `${reloj(duracion)} · `}
              {plural(cuentas.catalogo + cuentas.ia, 'renglón listo', 'renglones listos')} para agregar a tu hoja
            </b>
            <span>
              {cuentas.catalogo} {cuentas.catalogo === 1 ? 'sale' : 'salen'} de tus catálogos y {cuentas.ia} se {cuentas.ia === 1 ? 'genera' : 'generan'} con IA.
            </span>
            {(cuentas.complementos > 0 || cuentas.noDibujado > 0) && (
              <span>
                Son{' '}
                {[
                  plural(cuentas.principales, 'elemento del plano', 'elementos del plano'),
                  cuentas.complementos > 0 ? plural(cuentas.complementos, 'complementario', 'complementarios') : null,
                  cuentas.noDibujado > 0 ? `${cuentas.noDibujado} de lo que no trae el plano` : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                .
              </span>
            )}
            {cuentas.sinCantidad > 0 && (
              <b style={{ color: 'var(--bad)' }}>
                {plural(cuentas.sinCantidad, 'va sin cantidad', 'van sin cantidad')}: entran con 0 para que la completes.
              </b>
            )}
            {nFuera > 0 && <span>Además, {plural(nFuera, 'propuesta no se agrega', 'propuestas no se agregan')}; en cada una dice por qué.</span>}
            {nQuitados > 0 && <span>{plural(nQuitados, 'quitado', 'quitados')} por ti (tachados en la lista).</span>}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Qué renglones ver">
          {filtros.map((x) => (
            <Button
              key={x.k}
              size="small"
              appearance={filtro === x.k ? 'primary' : 'secondary'}
              disabled={x.n === 0 && x.k !== 'todos'}
              title={x.ayuda}
              onClick={() => setFiltro(x.k)}
            >
              {x.texto} ({x.n})
            </Button>
          ))}
        </div>
        {costo && <LineaStaff costo={costo} />}
        <div className={s.tablaCaja}>
          <table className={s.tabla}>
            <thead>
              <tr>
                <th className={s.th}>Del plano</th>
                <th className={s.th} style={{ minWidth: 340 }}>
                  Concepto
                </th>
                <th className={s.th} style={{ textAlign: 'right' }}>
                  Cantidad
                </th>
                <th className={s.th} style={{ textAlign: 'right' }}>
                  P. unitario
                </th>
                <th className={s.th} style={{ textAlign: 'right' }}>
                  Importe
                </th>
                <th className={s.th} style={{ width: '1%' }} aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {principales.map(filaTabla)}
              {visibles.length === 0 && (
                <tr>
                  <td colSpan={6} className={s.td} style={{ color: 'var(--text-tertiary)' }}>
                    No hay renglones en este filtro.
                  </td>
                </tr>
              )}
            </tbody>
            {noDib.length > 0 && (
              <tbody>
                <tr>
                  <td colSpan={6} className={s.seccion}>
                    Lo que no trae el plano (estimado con lo que el plano sí trae)
                  </td>
                </tr>
                {noDib.map(filaTabla)}
              </tbody>
            )}
          </table>
        </div>
        {sinSaldo && <Punto tono="mal">{sinSaldo}</Punto>}
        {cuentas.ia > 0 && (
          <div className={s.sub}>
            Los {cuentas.ia} de «Generar con IA» no se generan todavía: entran a la hoja marcados y sin precio. Al final te preguntamos si los generas.
          </div>
        )}
        {fallidos > 0 && (
          <div className={s.sub}>{plural(fallidos, 'consulta no tuvo respuesta', 'consultas no tuvieron respuesta')}: lo que falte, asígnalo desde la lista.</div>
        )}
      </div>
    );
  })();

  const listos = filas.filter(entra).length;
  return (
    <Dialog open={p.abierto} onOpenChange={(_e, d) => !d.open && paso !== 'armando' && paso !== 'agregando' && p.onCerrar()}>
      <DialogSurface className={s.surface}>
        <DialogBody>
          <DialogTitle>
            {paso === 'antes'
              ? 'Armar el presupuesto de este plano'
              : paso === 'borrador'
                ? 'Tienes un borrador'
                : paso === 'revision'
                ? 'Revisa antes de agregar a la hoja'
                : paso === 'listo'
                  ? 'Listo'
                  : 'Armando el presupuesto'}
          </DialogTitle>
          <DialogContent>{contenido}</DialogContent>
          <DialogActions>
            {paso === 'borrador' && borradorPrevio && (
              <>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    p.borrador.borrar();
                    setBorradorPrevio(null);
                    setPaso('antes');
                  }}
                >
                  Empezar de cero
                </Button>
                <Button appearance="primary" onClick={() => cargarBorrador(borradorPrevio)}>
                  Seguir con el borrador
                </Button>
              </>
            )}
            {paso === 'antes' && (
              <>
                <Button appearance="secondary" onClick={p.onCerrar}>
                  Cancelar
                </Button>
                <Button
                  appearance="primary"
                  icon={revisandoSaldo || esperandoMaterial ? <Spinner size="tiny" /> : <Sparkle20Regular />}
                  disabled={bloqueado || revisandoSaldo || esperandoMaterial}
                  onClick={() => void armar()}
                >
                  {esperandoMaterial ? 'Espera un momento…' : 'Armar'}
                </Button>
              </>
            )}
            {paso === 'armando' && (
              <Button appearance="secondary" onClick={() => (detener.current = true)}>
                Detener y revisar lo armado
              </Button>
            )}
            {paso === 'revision' && (
              <>
                {errorAgregar && (
                  <span style={{ flexBasis: '100%', fontSize: 12.5, color: 'var(--bad)' }}>{errorAgregar}</span>
                )}
                <Button
                  appearance="secondary"
                  onClick={() => {
                    p.borrador.borrar();
                    p.onCerrar();
                  }}
                >
                  Descartar
                </Button>
                <Button appearance="secondary" onClick={p.onCerrar} title="Se guarda como borrador: al volver a abrir te pregunta si lo retomas.">
                  Guardar borrador y cerrar
                </Button>
                <Button appearance="secondary" onClick={imprimir}>
                  Imprimir
                </Button>
                <Button
                  appearance="primary"
                  disabled={listos === 0}
                  title={p.rehacer ? `Quita los ${p.rehacer.renglones} renglones de la hoja de este plano y pone estos ${listos}.` : undefined}
                  onClick={() => void agregar()}
                >
                  {p.rehacer ? `Reemplazar la hoja (${listos})` : `Agregar ${listos} a la hoja`}
                </Button>
              </>
            )}
            {paso === 'listo' && resultado && (
              <>
                <Button appearance={resultado.conIa > 0 ? 'secondary' : 'primary'} onClick={p.onCerrar}>
                  {resultado.conIa > 0 ? 'Después' : 'Cerrar'}
                </Button>
                {resultado.conIa > 0 && (
                  <Button appearance="primary" icon={<LightbulbRegular />} onClick={p.generarConIa}>
                    Generar con IA ({resultado.conIa})
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
